import isNil from 'lodash/isNil';
import { useCallback, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import { useTypedDispatch, useTypedSelector } from 'store';
import { RootStore } from 'store';
import {
  EArbitrageFilters,
  ECexPairFilters,
  EDexPairFilters,
  EGeneralFilters,
  EProjectFilters,
  ESolPairFilters,
} from 'types/filters';
import { IFilterValue } from 'types/filters/common';
import { ICexPair, IDexPair } from 'types/pairs';
import { EExchange } from 'web3';

import { setupInitialFilters } from '..';
import { getProjectFiltersKey } from '../utils';

interface IProps {
  general?: EGeneralFilters;
  arbitrage?: EArbitrageFilters;
  cex?: {
    pair?: ICexPair;
    type: ECexPairFilters;
  };
  dex?: {
    pair?: IDexPair;
    type: EDexPairFilters;
  };
  sol?: {
    pair?: IDexPair;
    type: ESolPairFilters;
  };
  project?: {
    projectId?: number;
    type: EProjectFilters;
  };
}

export function useGetFilters({ cex, dex, sol, general, arbitrage, project }: IProps) {
  const dispatch = useTypedDispatch();
  const initializedKeysRef = useRef<Set<string>>(new Set());

  const pairStateSelector = useCallback(
    (store: RootStore) => {
      if (general) return store.filters[general];

      if (arbitrage) return store.filters.arbitrage[arbitrage];

      if (cex) {
        return store.filters.cex[cex.pair?.id ?? 0];
      }

      if (dex) {
        return store.filters.dex[dex.pair?.id ?? 0];
      }

      if (sol) {
        return store.filters.sol[sol.pair?.id ?? 0];
      }
    },
    [general, arbitrage, cex, dex, sol],
  );

  const pairState = useTypedSelector(pairStateSelector);
  const projectEntry = useTypedSelector((store: RootStore) => {
    if (!project?.projectId) return undefined;
    const key = getProjectFiltersKey(project.projectId, project.type);
    return store.filters.project[key];
  });

  const getFilters = useCallback((): IFilterValue[] => {
    try {
      const initialFilters = [{ id: uuid() }] as IFilterValue[];
      const initKey = general
        ? `general-${general}`
        : arbitrage
          ? `arbitrage-${arbitrage}`
          : project?.projectId
            ? `project-${project.projectId}-${project.type}`
            : cex
              ? `cex-${cex.pair?.id}-${cex.type}`
              : dex
                ? `dex-${dex.pair?.id}-${dex.type}`
                : sol
                  ? `sol-${sol.pair?.id}-${sol.type}`
                  : 'unknown';

      if (general && (!pairState?.filters || (pairState?.filters as any[]).length === 0)) {
        if (!initializedKeysRef.current.has(initKey)) {
          initializedKeysRef.current.add(initKey);
          setTimeout(() => {
            dispatch(
              setupInitialFilters({
                general,
                filters: {
                  filters: initialFilters,
                  setted: [],
                },
              }),
            );
          }, 0);
        }

        return initialFilters;
      }

      if (general && pairState) return (pairState.filters ?? []) as IFilterValue[];

      if (arbitrage && (!pairState?.filters || (pairState?.filters as any[]).length === 0)) {
        if (!initializedKeysRef.current.has(initKey)) {
          initializedKeysRef.current.add(initKey);
          setTimeout(() => {
            dispatch(
              setupInitialFilters({
                arbitrage,
                filters: {
                  filters: initialFilters,
                  setted: [],
                },
              }),
            );
          }, 0);
        }

        return initialFilters;
      }

      if (arbitrage && pairState) return (pairState.filters ?? []) as IFilterValue[];

      if (project && (!projectEntry || projectEntry.filters.length === 0)) {
        if (project.projectId && !initializedKeysRef.current.has(initKey)) {
          initializedKeysRef.current.add(initKey);
          setTimeout(() => {
            dispatch(
              setupInitialFilters({
                projectId: project.projectId!,
                projectType: project.type,
                filters: {
                  filters: initialFilters,
                  setted: [],
                },
              }),
            );
          }, 0);
        }

        return initialFilters;
      }

      if (project && projectEntry) {
        return projectEntry.filters ?? [];
      }

      if (!general && !arbitrage && !project && !pairState) {
        const pairId = cex ? cex.pair?.id : dex ? dex.pair?.id : sol ? sol.pair?.id : undefined;
        const type = cex ? cex.type : dex ? dex.type : sol ? sol.type : undefined;

        if (!isNil(pairId) && !isNil(type)) {
          if (!initializedKeysRef.current.has(initKey)) {
            initializedKeysRef.current.add(initKey);
            setTimeout(() => {
              dispatch(
                setupInitialFilters({
                  pairId,
                  exchange: cex ? EExchange.cex : dex ? EExchange.dex : EExchange.sol,
                  filters: {
                    [type]: {
                      filters: initialFilters,
                      setted: [],
                    },
                  },
                }),
              );
            }, 0);
          }
        }

        return initialFilters;
      }

      const pairId = cex ? cex.pair?.id : dex ? dex.pair?.id : sol ? sol.pair?.id : undefined;
      const type = cex ? cex.type : dex ? dex.type : sol ? sol.type : undefined;

      if (isNil(pairId) || isNil(type)) return initialFilters;

      //@ts-ignore
      const filtersByType = pairState.filters[type];

      if (!filtersByType) {
        if (!initializedKeysRef.current.has(initKey)) {
          initializedKeysRef.current.add(initKey);
          setTimeout(() => {
            dispatch(
              setupInitialFilters({
                pairId,
                exchange: cex ? EExchange.cex : dex ? EExchange.dex : EExchange.sol,
                //@ts-ignore
                filters: { ...pairState.filters, [type]: { filters: initialFilters, setted: [] } },
              }),
            );
          }, 0);
        }

        return initialFilters;
      }

      return filtersByType.filters ?? [];
    } catch (error) {
      // On any unexpected error (including recursion/stack issues), fall back to an empty filter set
      console.warn('useGetFilters: failed to resolve filters, returning empty set', error);
      return [];
    }
  }, [general, pairState, arbitrage, cex, dex, sol, dispatch, project, projectEntry]);

  return getFilters;
}
